import React, { FC } from "react";
import { sanitizeHtml } from "@1-now/booking-core";
import { fitInlineColors } from "utils/fitInlineColors";
import { linkifyHtml, linkifyText } from "utils/richLinks";
import { BONZAH_POLICY_LINKS } from "designs/Common/BonzahAcknowledgement/BonzahAcknowledgement";

/**
 * What a protection package or a Bonzah add-on covers, as the desktop sections
 * print it (CarListingDetailPage renderBonzahSection): the operator's own
 * description may be HTML, plain text or carry links. Written once here for
 * the phone steps (1NOW-11155); the desktop sections are left as they are.
 */
export const detailLines = (options: any): string[] =>
  Object.values(options ?? {}).flatMap((v: any) => (Array.isArray(v) ? v : [v])).filter(
    (v) => typeof v === "string" && v.trim() !== "",
  );

/** A detail line as plain words: a line the operator cleared in the editor ("<p><br></p>") has none. */
export const plainLine = (line: string): string => {
  if (!line.trim().startsWith("<")) return line;
  const el = document.createElement("div");
  el.innerHTML = sanitizeHtml(line);
  return (el.textContent ?? "").trim();
};

/** The line that names the deductible, if the package has one. */
export const deductibleLine = (options: any): string | null =>
  detailLines(options).find((l) => /deductible/i.test(l) && !l.trim().startsWith("<")) ?? null;

/**
 * One line of what a package covers: the operator's own words, with their paragraphs and line breaks, and
 * every web address, e-mail and phone number a link (1NOW-11204). Paragraphs are separated, and a link sits
 * above a row's tap area (`relative`), as the desktop rows lay a button over the whole row.
 */
export const Line: FC<{ text: string; bgColor?: string }> = ({ text, bgColor }) => {
  const rich = text.trim().startsWith("<");
  return (
    <span
      className={`${rich ? "block [&_p+p]:mt-2" : "whitespace-pre-line"} [&_a]:relative [&_a]:text-blue-500 [&_a]:underline`}
      style={{ wordBreak: "break-word" }}
      dangerouslySetInnerHTML={{
        __html: rich ? fitInlineColors(linkifyHtml(sanitizeHtml(text)), bgColor) : linkifyText(text),
      }}
    />
  );
};

/** `bonzahLinks`: a Bonzah package or add-on, whose terms the renter is pointed to. */
const ProductDetails: FC<{ product: any; bgColor?: string; bonzahLinks?: boolean }> = ({
  product,
  bgColor,
  bonzahLinks = product?.insurance_by_bonzah === true,
}) => {
  const lines = detailLines(product?.options);
  return (
    <div className="mt-2 text-sm text-text-color">
      {product?.insurance_by_bonzah === true ? (
        <ul className="list-disc space-y-1 pl-5">
          {lines.map((l, i) => (
            <li key={i}>
              <Line text={l} bgColor={bgColor} />
            </li>
          ))}
        </ul>
      ) : (
        <div className="space-y-2 [&>span]:block">
          {lines.map((l, i) => (
            <Line key={i} text={l} bgColor={bgColor} />
          ))}
        </div>
      )}
      {product?.pdf ? (
        <a
          href={product.pdf}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-block text-xs font-bold underline"
        >
          View coverage
        </a>
      ) : null}
      {bonzahLinks && <BonzahLinks />}
    </div>
  );
};

export const BonzahLinks: FC = () => (
  <p className="mt-3 text-xs opacity-80">
    Please review Bonzah&apos;s{" "}
    {BONZAH_POLICY_LINKS.map((link, idx) => (
      <React.Fragment key={link.href}>
        {idx > 0 && (idx === BONZAH_POLICY_LINKS.length - 1 ? ", and " : ", ")}
        <a href={link.href} target="_blank" rel="noopener noreferrer" className="underline">
          {link.short.toLowerCase()}
        </a>
      </React.Fragment>
    ))}{" "}
    before selecting.
  </p>
);

export default ProductDetails;
